
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component specifically for database/network errors
 * Provides a graceful fallback UI when data operations fail
 */
class DatabaseErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a data format error
    if (error.message && 
        (error.message.includes('undefined') || 
         error.message.includes('null') ||
         error.message.includes('not defined') ||
         error.message.includes('cannot read property'))) {
      console.error('Data format error caught:', error.message);
      return {
        hasError: true,
        error
      };
    }
    
    // Check if this is a "no data" error which should be ignored
    if (error.message && 
        (error.message.includes('no data available') || 
         error.message.includes('empty result') ||
         error.message.includes('No records found'))) {
      console.log('Ignoring empty data error:', error.message);
      return {
        hasError: false,
        error: null
      };
    }
    
    // Check for camera/media related errors and handle them differently
    if (error.message &&
        (error.message.includes('Permission denied') ||
         error.message.includes('NotAllowedError') ||
         error.message.includes('NotFoundError') ||
         error.message.includes('camera') ||
         error.message.includes('getUserMedia') ||
         error.message.includes('audioContext') ||
         error.message.includes('media') ||
         error.message.includes('QR') ||
         error.message.includes('scan'))) {
      console.log('Media/camera error:', error.message);
      // We still want to show the UI but not as a database error
      return {
        hasError: false,
        error: null
      };
    }
    
    console.error('Database error caught:', error.message);
    
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Database error boundary caught an error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
    
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Return custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Return default fallback UI
      return (
        <Alert variant="destructive" className="my-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lỗi dữ liệu</AlertTitle>
          <AlertDescription className="mt-2">
            <p className="mb-2">
              Đã xảy ra lỗi khi xử lý dữ liệu. Vui lòng thử lại sau.
            </p>
            {this.state.error && (
              <p className="text-xs opacity-70 mb-3">
                {this.state.error.message}
              </p>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="bg-white"
              onClick={this.handleReset}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default DatabaseErrorBoundary;
